import { Stack, Typography } from '@mui/material';
import type { AccessCondition, Role } from '@repo/api-gw-sdk';
import React from 'react';

import {
  OptionMenu,
  menuItemStyles,
  type OptionMenuItemType,
} from '@/components/option-menu/option-menu';
import { Icon } from '@/components/shared/icon';
import type { PermissionDescriptor } from '@/contexts/permissions';

import { getAssignedRoleCondition } from './utils';

export const PermissionOptionMenu = ({
  icon,
  isPermissionAuthorized,
  permission,
  role,
  onBlocked,
  onAllowed,
  createRoleCondition,
  assignRoleCondition,
}: {
  role: Role;
  permission: PermissionDescriptor;
  isPermissionAuthorized: boolean;
  icon: React.ReactNode;
  onBlocked: () => void;
  onAllowed: () => void;
  createRoleCondition: () => void;
  assignRoleCondition: (roleCondition: AccessCondition) => void;
}) => {
  const assignedRoleCondition = getAssignedRoleCondition(permission, role);

  const allConditions = Object.values(role.accessConditions || {});

  const options = usePermissionOptions(
    assignedRoleCondition,
    isPermissionAuthorized,
    permission.allowConditions,
    allConditions
  );

  return (
    <OptionMenu
      iconButtonProps={{ className: 'p-0' }}
      icon={icon}
      options={options}
      onOptionSelected={(option) => {
        if (typeof option !== 'string') {
          const chosenIndex = options.indexOf(option);
          if (chosenIndex === 0) {
            onBlocked();
          } else if (chosenIndex === options.length - 1) {
            onAllowed();
          } else {
            if (option.subMenu?.length || option.divider) {
              return;
            }

            if (option.id === 'create-new-condition') {
              createRoleCondition();
            } else {
              assignRoleCondition(
                allConditions.find((x) => x.id === option.id)!
              );
            }
          }
        }
      }}
    />
  );
};

const usePermissionOptions = (
  assignedRoleCondition: AccessCondition | undefined,
  isPermissionAuthorized: boolean,
  isConditionalPermission: boolean | undefined,
  allConditions: AccessCondition[]
): OptionMenuItemType[] => {
  const selectedIndex = isConditionalPermission
    ? assignedRoleCondition
      ? 1
      : isPermissionAuthorized
        ? 2
        : 0
    : isPermissionAuthorized
      ? 1
      : 0;

  return [
    {
      icon: 'material-symbols-close-rounded',
      text: 'Blocked',
      id: 'blocked-option',
    },
    ...(isConditionalPermission
      ? [
          {
            id: 'create-new-condition',
            icon: 'material-symbols-rule-rounded',
            text: 'Conditional',
            subMenu: allConditions.length
              ? [
                  ...allConditions.map((x, index) => ({
                    id: x.id,
                    menuItemProps: {
                      sx: menuItemStyles(
                        !!(
                          assignedRoleCondition &&
                          allConditions.indexOf(assignedRoleCondition) === index
                        )
                      ),
                    },
                    text: x.id,
                  })),
                  { divider: true, id: 'divider' },
                  {
                    id: 'create-new-condition',
                    menuItemProps: {
                      sx: menuItemStyles(false),
                    },
                    text: (
                      <Stack alignItems='center' gap='4px' direction='row'>
                        <Icon
                          iconClass={'material-symbols-add-rounded'}
                          sx={{
                            '& i': {
                              fontWeight: '600',
                              color: 'currentColor',
                            },
                          }}
                        />
                        <Typography
                          className='pr-[8px]'
                          color='currentColor'
                          sx={{ fontWeight: '600' }}
                        >
                          Create Access Condition
                        </Typography>
                      </Stack>
                    ),
                  },
                ]
              : undefined,
          },
        ]
      : []),

    {
      icon: 'material-symbols-check',
      text: 'Allowed',
      id: 'allowed-option',
    },
  ].map((x, index) => ({
    subMenu: x.subMenu,
    menuItemProps: {
      sx: menuItemStyles(selectedIndex === index),
    },
    text: x.text,
    icon: (
      <Icon
        iconClass={x.icon}
        sx={{
          '& i': {
            color: 'currentColor',
          },
        }}
      />
    ),
    id: x.id,
  }));
};
